import React, { useCallback, useReducer, useMemo } from 'react';
import { Button } from '@packages/ui/primitives';
import type { FormActionButtonNode } from '../../types/layout';
import { ActionRegistry } from '@packages/table-builder/registry';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';

interface FormActionButtonRendererProps {
  node: FormActionButtonNode;
}

interface ActionButtonState {
  remainingSeconds: number;
  isLoading: boolean;
}

type ActionButtonAction =
  | { type: 'START_COOLDOWN'; seconds: number }
  | { type: 'TICK' }
  | { type: 'RESET_COOLDOWN' }
  | { type: 'LOADING_START' }
  | { type: 'LOADING_END' };

function actionButtonReducer(state: ActionButtonState, action: ActionButtonAction): ActionButtonState {
  switch (action.type) {
    case 'START_COOLDOWN':
      return { ...state, remainingSeconds: action.seconds };
    case 'TICK':
      return state.remainingSeconds <= 1
        ? { ...state, remainingSeconds: 0 }
        : { ...state, remainingSeconds: state.remainingSeconds - 1 };
    case 'RESET_COOLDOWN':
      return { ...state, remainingSeconds: 0 };
    case 'LOADING_START':
      return { ...state, isLoading: true };
    case 'LOADING_END':
      return { ...state, isLoading: false };
  }
}

function serializeFormValues(values: Record<string, unknown>): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value == null) continue;
    if (typeof value === 'string') {
      params[key] = value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      params[key] = String(value);
    } else {
      params[key] = JSON.stringify(value);
    }
  }
  return params;
}

export const FormActionButtonRenderer = React.memo(function FormActionButtonRenderer({
  node,
}: FormActionButtonRendererProps): React.ReactElement {
  const { form } = useFormEngineForm();
  const { params: blockParams } = useFormEngineState();
  const [state, dispatch] = useReducer(actionButtonReducer, { remainingSeconds: 0, isLoading: false });

  React.useEffect(() => {
    if (state.remainingSeconds <= 0) return;
    const timer = setInterval(() => dispatch({ type: 'TICK' }), 1000);
    return () => clearInterval(timer);
  }, [state.remainingSeconds]);

  const handleClick = useCallback(async () => {
    if (state.remainingSeconds > 0 || state.isLoading) return;

    const values = form.getValues() as Record<string, unknown>;

    if (node.externalRoute) {
      ActionRegistry.getNavigate()?.(node.externalRoute);
    }

    dispatch({ type: 'LOADING_START' });
    try {
      await ActionRegistry.executeData(node.actionId, { ...blockParams, ...serializeFormValues(values) });
    } finally {
      dispatch({ type: 'LOADING_END' });
    }

    if (node.cooldownSeconds && node.cooldownSeconds > 0) {
      dispatch({ type: 'START_COOLDOWN', seconds: node.cooldownSeconds });
    }
  }, [state.remainingSeconds, state.isLoading, form, node.actionId, node.cooldownSeconds, node.externalRoute, blockParams]);

  const buttonLabel = useMemo(() => {
    if (state.remainingSeconds > 0) {
      return `${node.label} (${state.remainingSeconds}s)`;
    }
    return node.label;
  }, [state.remainingSeconds, node.label]);

  const isDisabled = state.remainingSeconds > 0;

  return (
    <div style={{ display: 'flex', justifyContent: node.align === 'start' ? 'flex-start' : 'flex-end' }}>
      <Button
        variant={(node.variant as any) ?? 'primary-outline'}
        icon={node.icon}
        label={buttonLabel}
        onClick={handleClick}
        disabled={isDisabled}
        loading={state.isLoading}
        type='button'
      />
    </div>
  );
});
